import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  Edit,
  Loader2,
  MessageCircle,
  MoreVertical,
  StickyNote,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const NOTE_MAX_LENGTH = 150;
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useDeleteMealEntry,
  useUpdateMealEntry,
} from "@/hooks/useNutritionEvaluation";

interface NoteRowProps {
  /**
   * Entry id the note maps to:
   *  - `isAttached`: the parent food entry (tube feed comment field)
   *  - else:        the standalone `is_comment` entry
   */
  entryId: string;
  comment: string;
  /**
   * Tube feed: note lives as `comment` on a food entry. Delete clears the
   * field via PATCH (the food entry stays). Else: standalone is_comment
   * entry — delete removes the entry entirely.
   */
  isAttached: boolean;
  appointmentId: string;
  isTubeFeeding: boolean;
  isMealPlan: boolean;
  isCompleted: boolean;
  mealGroupLabel?: string;
}

export const NoteRow: React.FC<NoteRowProps> = ({
  entryId,
  comment,
  isAttached,
  appointmentId,
  isTubeFeeding,
  isMealPlan,
  isCompleted,
  mealGroupLabel,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [editValue, setEditValue] = useState(comment);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateEntry = useUpdateMealEntry(
    appointmentId,
    isTubeFeeding,
    isMealPlan
  );
  const deleteEntry = useDeleteMealEntry(
    appointmentId,
    isTubeFeeding,
    isMealPlan
  );

  useEffect(() => {
    if (!editOpen) setEditValue(comment);
  }, [comment, editOpen]);

  const handleSaveEdit = () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === comment) {
      setEditOpen(false);
      return;
    }
    updateEntry.mutate(
      { entryId, data: { comment: trimmed } },
      { onSuccess: () => setEditOpen(false) }
    );
  };

  const handleConfirmDelete = () => {
    if (isAttached) {
      updateEntry.mutate(
        { entryId, data: { comment: "" } },
        { onSuccess: () => setShowDeleteConfirm(false) }
      );
    } else {
      deleteEntry.mutate(entryId, {
        onSuccess: () => setShowDeleteConfirm(false),
      });
    }
  };

  const isPending = updateEntry.isPending || deleteEntry.isPending;
  const fromPart = mealGroupLabel ? `"${mealGroupLabel}"` : "this group";

  return (
    <>
      <div className="flex items-start gap-3 px-4 py-2.5 bg-muted/60 rounded-2xl group">
        <MessageCircle className="size-4 shrink-0 mt-0.5 fill-muted-foreground/70 text-muted-foreground/70" />
        <span className="z-body text-foreground/85 flex-1 break-words whitespace-pre-wrap">
          {comment}
        </span>
        {!isCompleted && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Note actions"
                className="shrink-0"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setEditOpen(true);
                }}
              >
                <Edit className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setShowDeleteConfirm(true);
                }}
                disabled={isPending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
          <DialogHeader className="p-6 pb-4 shrink-0">
            <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
              <StickyNote className="h-5 w-5 text-primary" />
              Edit Note
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 pb-4 bg-background space-y-2">
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={5}
              className="resize-none"
              autoFocus
            />
            <p
              className={cn(
                "z-caption text-right",
                editValue.length > NOTE_MAX_LENGTH
                  ? "text-destructive font-medium"
                  : "text-muted-foreground"
              )}
            >
              {NOTE_MAX_LENGTH - editValue.length} characters left
            </p>
          </div>

          <DialogFooter className="p-6 mt-auto border-t border-border/60 bg-background flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={updateEntry.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={
                !editValue.trim() ||
                editValue.trim() === comment ||
                editValue.length > NOTE_MAX_LENGTH ||
                updateEntry.isPending
              }
            >
              {updateEntry.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Remove the note from {fromPart}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleConfirmDelete();
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
